import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  AlertService,
  BLOCK_EXPLORER_TX_LINK,
  ChainRepository,
  ChainState,
  FrontendAddressUtil,
  ModalComponent,
} from 'common';
import { TranslateModule } from '@ngx-translate/core';
import { NgIconComponent, provideIcons } from '@ng-icons/core';
import { heroInformationCircleMini } from '@ng-icons/heroicons/mini';
import {
  EnzymeRepository,
  PriceCompareRepository,
  RebalancingRepository,
  RebalancingState,
  TransactionExecutionRepository,
  TransactionExecutionState,
} from '../../../repository';
import { NgxFloatUiDirective } from 'ngx-float-ui';
import { Clipboard } from '@angular/cdk/clipboard';
import { Router } from '@angular/router';
import { first, skip } from 'rxjs';
import { TransactionExecutionStep } from '../../../enum';
import { EnzymeGasRelayerService, RebalancingService } from '../../../service';
import { RebalancingStep } from '../rebalancing-step.enum';

@Component({
  selector: 'common-rebalancing-execution-modal',
  standalone: true,
  imports: [
    CommonModule,
    ModalComponent,
    TranslateModule,
    NgIconComponent,
    NgxFloatUiDirective,
  ],
  providers: [
    provideIcons({
      heroInformationCircleMini,
    }),
  ],
  templateUrl: './execution-modal.component.html',
  styleUrls: ['./execution-modal.component.scss'],
})
export class ExecutionModalComponent {
  public getShortDisplayAddress = FrontendAddressUtil.getDisplayAddress;

  @Input()
  public isOpen = false;

  @Output()
  public isOpenChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  public Step = TransactionExecutionStep;
  public step: TransactionExecutionStep | undefined;
  public failedStep: TransactionExecutionStep | undefined;
  public waitForQuoteRefresh = false;

  constructor(
    private readonly router: Router,
    private readonly clipboard: Clipboard,
    private readonly alertService: AlertService,
    private readonly rebalancingService: RebalancingService,
    private readonly enzymeGasRelayerService: EnzymeGasRelayerService,
    private readonly rebalancingRepository: RebalancingRepository,
    private readonly transactionExeuctionRepository: TransactionExecutionRepository,
    private readonly chainRepository: ChainRepository,
    private readonly enzymeRepository: EnzymeRepository,
    private readonly priceCompareRepository: PriceCompareRepository,
  ) {
    this.subscribeToStepChange();
  }

  public get rebalancingState(): RebalancingState {
    return this.rebalancingRepository.store.getValue();
  }

  public get executionState(): TransactionExecutionState {
    return this.transactionExeuctionRepository.store.getValue();
  }

  public get chainState(): ChainState {
    return this.chainRepository.store.getValue();
  }

  private subscribeToStepChange(): void {
    this.transactionExeuctionRepository.step$.subscribe(step => {
      this.step = step;
    });
    this.transactionExeuctionRepository.failedStep$.subscribe(failedStep => {
      this.failedStep = failedStep;
    });
  }

  public getTenderlySimulationLink(): string {
    if (!this.executionState.tenderlySimulation) {
      return '';
    }
    return this.executionState.tenderlySimulation.getPublicUrl();
  }

  public onExecuteAfterRefreshClick(): void {
    this.waitForQuoteRefresh = true;
    this.transactionExeuctionRepository.setStep(
      TransactionExecutionStep.EXECUTE,
    );
    this.transactionExeuctionRepository.setFailedStep(undefined);

    this.rebalancingRepository.rebalancing$
      .pipe(
        skip(1), // Skip current rebalancing
        first(),
      )
      .subscribe(rebalancing => {
        this.waitForQuoteRefresh = false;
        if (rebalancing) {
          void this.execute();
        }
      });
  }

  public onExecuteClick(): void {
    void this.execute();
  }

  public onExecuteWithWalletClick(): void {
    void this.executeWithWallet();
  }

  private async execute(): Promise<void> {
    if (this.executionState.isGasRelayerTx) {
      void this.executeWithRelayer();
    } else {
      void this.executeWithWallet();
    }
  }

  private async executeWithWallet(): Promise<void> {
    await this.rebalancingService.executeRebalancing();
  }

  private async executeWithRelayer(): Promise<void> {
    await this.enzymeGasRelayerService.executeViaReleayer();
  }

  public onRecalculateClick(): void {
    this.rebalancingRepository.setRebalancing(undefined);
    this.rebalancingRepository.setPriceChangeWarning(false);
    this.rebalancingRepository.setCalculationProgress(undefined);
    this.rebalancingRepository.setStep(RebalancingStep.CALCULATE);
    this.rebalancingService.calculateRebalancing();
    this.close();
  }

  public copyToClipboard(data: string): void {
    this.clipboard.copy(data);
    this.alertService.showInfo('global.copiedToClipboard');
  }

  public getBlockExplorerLink(): string {
    if (!this.executionState.transaction) {
      return '';
    }
    return BLOCK_EXPLORER_TX_LINK(
      this.executionState.transaction.hash,
      this.chainState.chain,
    );
  }

  public getTenderlyLink(): string {
    if (!this.executionState.transaction) {
      return '';
    }
    return `https://dashboard.tenderly.co/tx/${this.chainState.chain?.getIdentifierAsDecimal()}/${
      this.executionState.transaction.hash
    }`;
  }

  public onMakeNewTradeClick(): void {
    this.reset();
    this.close();
  }

  private reset(): void {
    this.enzymeRepository.resetSelectedVault();
    this.rebalancingRepository.reset();
    this.priceCompareRepository.store.reset();
    this.router.navigate(['/rebalance']);
  }

  public onCloseClick(): void {
    this.close();
  }

  private close(): void {
    this.isOpen = false;
    this.isOpenChange.emit(false);
  }
}
